/**
 * Plan Service - Single source of truth for all plan-related logic
 *
 * This module centralizes plan categorization, validation requirements,
 * and plan-specific business logic that was previously duplicated across:
 * - validation.js
 * - gtmTracking.js
 * - SignUp.js
 * - constants/index.js
 */

// =============================================================================
// Plan Category Definitions (Single Source of Truth)
// =============================================================================

export const LENDER_PLANS = [
  'hb-lender-starter',
  'hb-lender-pro',
  'hb-lender-unlimited'
];

export const LENDER_CORPORATE_PLANS = [
  'fairway-unlimited-pro',
  'fairway-unlimited-starter',
  'edge-preferred-broker-pro',
  'edge-preferred-broker-starter',
  'evergreen-preferred-broker-pro',
  'evergreen-preferred-broker-starter',
  'west-preferred-broker-pro',
  'west-preferred-broker-starter',
  'union-preferred-broker-pro',
  'union-preferred-broker-starter',
  'rocket-preferred-broker-pro',
  'rocket-preferred-broker-starter',
  'barrett-preferred-broker-pro',
  'barrett-preferred-broker-starter',
  'arbor-financial-group-preferred-broker-pro',
  'arbor-financial-group-preferred-broker-starter',
  'c2-preferred-broker-pro',
  'c2-preferred-broker-starter',
  'xpert-preferred-broker-pro',
  'xpert-preferred-broker-starter',
  'emortgage-preferred-broker-pro',
  'emortgage-preferred-broker-starter',
  'vip-preferred-broker-pro',
  'vip-preferred-broker-starter',
  'all-western-preferred-broker-pro',
  'all-western-preferred-broker-starter',
  'umortgage-preferred-broker-pro',
  'umortgage-preferred-broker-starter'
];

export const REAL_ESTATE_PLANS = [
  'freemium-starter',
  'hb-agent-solo',
  'hb-agent-partner',
  'hb-agent-team'
];

export const INSURANCE_PLANS = ['hb-insurance-agent-pro'];

export const TITLE_PLANS = ['hb-title-agent-pro'];

// Plans that only support monthly billing (no annual option)
export const MONTHLY_ONLY_PLANS = [
  ...REAL_ESTATE_PLANS,
  ...INSURANCE_PLANS,
  ...TITLE_PLANS,
  ...LENDER_CORPORATE_PLANS
];

// Plans that require subscription length selection
export const SUBSCRIPTION_LENGTH_REQUIRED_PLANS = [
  'hb-lender-starter',
  'hb-lender-pro',
  'hb-lender-unlimited'
];

// =============================================================================
// Plan Category Check Functions
// =============================================================================

/**
 * Check if a plan is a lender plan
 * @param {string} plan - Plan code
 * @returns {boolean}
 */
export const isLenderPlan = (plan) => LENDER_PLANS.includes(plan) || LENDER_CORPORATE_PLANS.includes(plan);

/**
 * Check if a plan is a real estate agent plan
 * @param {string} plan - Plan code
 * @returns {boolean}
 */
export const isRealEstatePlan = (plan) => REAL_ESTATE_PLANS.includes(plan);

/**
 * Check if a plan is an insurance agent plan
 * @param {string} plan - Plan code
 * @returns {boolean}
 */
export const isInsurancePlan = (plan) => INSURANCE_PLANS.includes(plan);

/**
 * Check if a plan is a title agent plan
 * @param {string} plan - Plan code
 * @returns {boolean}
 */
export const isTitlePlan = (plan) => TITLE_PLANS.includes(plan);

/**
 * Check if a plan is monthly-only (no annual option)
 * @param {string} plan - Plan code
 * @returns {boolean}
 */
export const isMonthlyOnlyPlan = (plan) => MONTHLY_ONLY_PLANS.includes(plan);

/**
 * Check if a plan requires subscription length selection
 * @param {string} plan - Plan code
 * @returns {boolean}
 */
export const requiresSubscriptionLength = (plan) => SUBSCRIPTION_LENGTH_REQUIRED_PLANS.includes(plan);

// =============================================================================
// Plan Type & Section Functions (for tracking/analytics)
// =============================================================================

/**
 * Get the plan type category for a plan
 * @param {string} plan - Plan code
 * @returns {string} Plan type: 'lender', 'real_estate', 'insurance_agent', 'title_agent', or 'unknown'
 */
export const getPlanType = (plan) => {
  if (isLenderPlan(plan)) return 'lender';
  if (isRealEstatePlan(plan)) return 'real_estate';
  if (isInsurancePlan(plan)) return 'insurance_agent';
  if (isTitlePlan(plan)) return 'title_agent';
  return 'unknown';
};

/**
 * Get the form section identifier for a plan (used in GTM tracking)
 * @param {string} plan - Plan code
 * @returns {string} Form section identifier
 */
export const getFormSection = (plan) => {
  if (isRealEstatePlan(plan)) return 'real_estate_signup';
  if (isLenderPlan(plan)) return 'lender_signup';
  if (isInsurancePlan(plan)) return 'insurance_agent_signup';
  if (isTitlePlan(plan)) return 'title_agent_signup';
  return 'unknown';
};

// =============================================================================
// Plan Identifier Functions
// =============================================================================

/**
 * Get the identifier type required for a plan
 * @param {string} plan - Plan code
 * @returns {string} Identifier type: 'nmls', 'state_license', 'insurance', 'title', or 'unknown'
 */
export const getIdentifierType = (plan) => {
  if (isLenderPlan(plan)) return 'nmls';
  if (isRealEstatePlan(plan)) return 'state_license';
  if (isInsurancePlan(plan)) return 'insurance';
  if (isTitlePlan(plan)) return 'title';
  return 'unknown';
};

/**
 * Get the identifier token from form data based on plan type
 * @param {string} plan - Plan code
 * @param {Object} formData - Form data containing nmlsId and stateLicense
 * @returns {string|null} The identifier token or null
 */
export const getIdentifierToken = (plan, formData) => {
  if (isLenderPlan(plan)) return formData.nmlsId || null;
  if (isRealEstatePlan(plan)) return formData.stateLicense || null;
  return null;
};

// =============================================================================
// Plan Code Utilities
// =============================================================================

/**
 * Get the full plan code including subscription length suffix
 * @param {string} plan - Base plan code
 * @param {string} subscriptionLength - Subscription length ('monthly' or 'v2-annual')
 * @param {Array} companyPlans - Optional array of company-specific plans
 * @returns {string} Full plan code
 */
export const getFullPlanCode = (plan, subscriptionLength, companyPlans = []) => {
  // Check if this is a company-specific plan (from API)
  const isCompanyPlan = companyPlans.some(p => {
    const code = p.attributes?.code || p.attributes?.['plan-code'];
    return code === plan;
  });

  // Company plans and monthly-only plans don't get a suffix
  if (isCompanyPlan || isMonthlyOnlyPlan(plan)) {
    return plan;
  }

  return `${plan}-${subscriptionLength}`;
};

// =============================================================================
// Validation Requirement Functions
// =============================================================================

/**
 * Check if NMLS ID is required for a plan
 * @param {string} plan - Plan code
 * @returns {boolean}
 */
export const requiresNmlsId = (plan) => isLenderPlan(plan);

/**
 * Check if state license is required for a plan
 * @param {string} plan - Plan code
 * @returns {boolean}
 */
export const requiresStateLicense = (plan) => isRealEstatePlan(plan);

/**
 * Get all required fields for a plan
 * @param {string} plan - Plan code
 * @param {string} paymentMethod - Payment method ('card' or 'bank')
 * @returns {Object} Object with field names as keys and boolean values
 */
export const getRequiredFields = (plan, paymentMethod) => {
  const baseFields = {
    firstName: true,
    lastName: true,
    email: true,
    termsAccepted: true
  };

  // Plan-specific fields
  if (isLenderPlan(plan)) {
    baseFields.nmlsId = true;
    if (requiresSubscriptionLength(plan)) {
      baseFields.subscriptionLength = true;
    }
  } else if (isRealEstatePlan(plan)) {
    baseFields.stateLicense = true;
  }

  // Payment method fields
  if (paymentMethod === 'card') {
    Object.assign(baseFields, {
      cardFirstName: true,
      cardLastName: true,
      billingAddress: true,
      billingCity: true,
      billingState: true,
      billingZip: true
    });
  } else if (paymentMethod === 'bank') {
    Object.assign(baseFields, {
      accountType: true,
      routingNumber: true,
      accountNumber: true,
      accountNumberConfirmation: true,
      billingAddress: true,
      billingCity: true,
      billingState: true,
      billingZip: true
    });
  }

  return baseFields;
};
